import React, { useState, useEffect, useMemo } from 'react';
import { MoreVertical, SlidersHorizontal, X } from 'lucide-react';
import { RequestsCalendarView } from './RequestCalenderView';
import { useAdminRequests } from '../../graphql/hooks/useAdminDashboard';
import { ConfirmModal } from '../core';
import { useDeleteTracking } from '../../graphql/hooks/useAdminDashboard';
import type { AdminRequestItem } from '../../graphql/types/admin_dashboard';
import { useQueryClient } from '@tanstack/react-query';
import { DEFAULT_ENDPOINT_URL_EXPORT } from '../../graphql/client';
import { useActiveUsers } from '../../graphql/hooks/useUserDashboard';
import { EditRequestModal } from '../UserRequestsView';
import type { UserRequestItem } from '../../graphql/types/timeoff';
import toast from "react-hot-toast";
import { RequestsFilterModal } from './RequestsFilterModal';
import {
  EMPTY_REQUEST_FILTERS,
  REQUEST_STATUS_LABELS,
  REQUEST_TYPE_LABELS,
  countActiveFilters,
  formatDayMonthYear,
  toRequestVariables,
  type RequestFilters,
  type RequestsPreset,
} from './requestFilters';

type RequestsAdminViewTabProps = {
  preset?: RequestsPreset | null;
};

export const RequestsAdminViewTab = ({ preset }: RequestsAdminViewTabProps) => {

  const queryClient = useQueryClient();
  const [requestView, setRequestView] = useState<'list' | 'calendar'>('list');
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [calendarView, setCalendarView] = useState<'Month' | 'Week' | 'Day'>('Month');

  const [approvedPage, setApprovedPage] = useState(1);
  const [pendingPage, setPendingPage] = useState(1);
  const [pendingItemsPerPage, setPendingItemsPerPage] = useState(10);
  const [approvedItemsPerPage, setApprovedItemsPerPage] = useState(10);
  const [editModalEntry, setEditModalEntry] = useState<UserRequestItem | null>(null);

  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [deleteRequestModalOpen, setDeleteRequestModalOpen] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState<AdminRequestItem | null>(null);

  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [requestToProcess, setRequestToProcess] = useState<AdminRequestItem | null>(null);

  const [pendingDropdownId, setPendingDropdownId] = useState<string | null>(null);

  const { mutate: deleteRequest } = useDeleteTracking();

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const [filters, setFilters] = useState<RequestFilters>({ ...EMPTY_REQUEST_FILTERS, ...preset });
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  const activeFilterCount = countActiveFilters(filters);
  const paging = { approvedPage, approvedLimit: approvedItemsPerPage, pendingPage, pendingLimit: pendingItemsPerPage };

  const { data } = useAdminRequests({ ...paging, search, ...toRequestVariables(filters) });
  const { data: users } = useActiveUsers();

  const userOptions = useMemo(
    () => (users ?? []).map((user) => ({ value: user.id, label: user.full_name })),
    [users]
  );

  const approved = data?.getAdminRequests.approved;
  const pending = data?.getAdminRequests.pending;
  const approvedRequests = approved?.data ?? [];
  const approvedPaginator = approved?.paginatorInfo;
  const pendingRequests = pending?.data ?? [];
  const pendingPaginator = pending?.paginatorInfo;

  const applyFilters = (next: RequestFilters) => {
    setFilters(next);
    setApprovedPage(1);
    setPendingPage(1);
    setFilterModalOpen(false);
  };

  const resetFilters = () => applyFilters(EMPTY_REQUEST_FILTERS);

  const clearFilter = (patch: Partial<RequestFilters>) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setApprovedPage(1);
    setPendingPage(1);
  };

  // one removable chip per active filter
  const filterChips: { key: string; label: string; onRemove: () => void }[] = [
    ...(filters.userId
      ? [{
          key: 'user',
          label: users?.find((u) => String(u.id) === filters.userId)?.full_name ?? 'Benutzer',
          onRemove: () => clearFilter({ userId: '' }),
        }]
      : []),
    ...(filters.type !== 'all'
      ? [{ key: 'type', label: REQUEST_TYPE_LABELS[filters.type], onRemove: () => clearFilter({ type: 'all' }) }]
      : []),
    ...(filters.from || filters.to
      ? [{
          key: 'range',
          label: filters.from && filters.to
            ? `${formatDayMonthYear(filters.from)} – ${formatDayMonthYear(filters.to)}`
            : filters.from
            ? `ab ${formatDayMonthYear(filters.from)}`
            : `bis ${formatDayMonthYear(filters.to)}`,
          onRemove: () => clearFilter({ from: '', to: '' }),
        }]
      : []),
    ...(filters.status !== 'all'
      ? [{
          key: 'status',
          label: `Frühere: ${REQUEST_STATUS_LABELS[filters.status]}`,
          onRemove: () => clearFilter({ status: 'all' }),
        }]
      : []),
  ];

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setApprovedPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const mapTrackingTypeToEnum = (type: string): 'HOLIDAY' | 'OVERTIME' | 'SICK' | 'TIME' => {
    switch (type) {
      case 'holiday': return 'HOLIDAY';
      case 'overtime': return 'OVERTIME';
      case 'sick': return 'SICK';
      case 'time': return 'TIME';
      default: throw new Error(`Unknown tracking type: ${type}`);
    }
  };

  const getRequestTypeLabel = (type?: string) => {
    switch (type) {
      case 'holiday': return 'Urlaub';
      case 'sick': return 'Krank';
      case 'overtime': return 'Überstunden';
      default: return '-';
    }
  };

  const getStatusBadge = (status?: number) => {
    if (status === 2) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[12px] font-medium bg-red-50 text-red-600 border border-red-200">
          Abgelehnt
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[12px] font-medium bg-green-50 text-green-600 border border-green-200">
        Genehmigt
      </span>
    );
  };

  const formatDate = (date: string) => {
    const [year, month, day] = date.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString('de-DE', {
      day: 'numeric', month: 'long', year: 'numeric',
    });
  };

  const parseEditPayload = (payload?: string | null) => {
    if (!payload) return null;
    try { return JSON.parse(payload); } catch { return null; }
  };

  const formatDateShort = (isoDate?: string | null) => {
    if (!isoDate) return null;
    const [year, month, day] = isoDate.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString('de-DE', {
      day: 'numeric', month: 'long', year: 'numeric',
    });
  };

  const formatCreatedAt = (createdAt?: string | null) => {
    if (!createdAt) return '-';
    return new Date(createdAt).toLocaleDateString('de-DE', {
      day: 'numeric', month: 'short', year: 'numeric',
    });
  };

  const handleDeleteClick = (entry: AdminRequestItem) => {
    setRequestToDelete(entry);
    setDeleteRequestModalOpen(true);
    setOpenMenuId(null);
  };

  const handleApproveClick = (entry: AdminRequestItem) => {
    setRequestToProcess(entry);
    setApproveModalOpen(true);
    setOpenMenuId(null);
  };

  const handleRejectClick = (entry: AdminRequestItem) => {
    setRequestToProcess(entry);
    setRejectModalOpen(true);
    setOpenMenuId(null);
  };

  const getTrackingBasePath = (type?: string) => {
    switch (type) {
      case 'holiday': return 'holiday';
      case 'overtime': return 'overtime';
      default: throw new Error(`Unsupported tracking type: ${type}`);
    }
  };

  const isRequestInPast = (endDate: string) => {
    const [year, month, day] = endDate.split('-').map(Number);
    const end = new Date(year, month - 1, day);
    end.setHours(23, 59, 59, 999);
    return end < new Date();
  };

  const handleEditClick = (entry: AdminRequestItem) => {
    if (isRequestInPast(entry.end_date)) return;
    const substitute = entry.substitute_id && entry.substitute
      ? { id: String(entry.substitute_id), full_name: entry.substitute }
      : null;
    setEditModalEntry({
      id: entry.id,
      type: entry.type,
      start_date: entry.start_date,
      end_date: entry.end_date,
      substitute,
    } as UserRequestItem);
    setOpenMenuId(null);
    setPendingDropdownId(null);
  };

  const toastSuccess = (message: string) =>
    toast.success(message, {
      duration: 3000,
      style: { background: "#f0fdf4", border: "1px solid #bbf7d0", color: "#15803d", padding: "12px 14px", borderRadius: "8px", fontSize: "14px" },
      iconTheme: { primary: "#16a34a", secondary: "#f0fdf4" },
    });

  const toastError = (message: string) =>
    toast.error(message, {
      duration: 4000,
      style: { background: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626", padding: "12px 14px", borderRadius: "8px", fontSize: "14px" },
      iconTheme: { primary: "#dc2626", secondary: "#fef2f2" },
    });

  const handleApproveConfirm = async () => {
    if (!requestToProcess?.type || !requestToProcess?.token) return;

    if (requestToProcess.request_type === 1) {
      deleteRequest(
        { id: requestToProcess.id, type: mapTrackingTypeToEnum(requestToProcess.type) },
        {
          onSuccess: () => toastSuccess("Löschantrag bestätigt"),
          onError: () => toastError("Löschantrag konnte nicht bestätigt werden"),
        }
      );
      await queryClient.invalidateQueries({ queryKey: ['admin-requests'] });
      setApproveModalOpen(false);
      setRequestToProcess(null);
      setPendingDropdownId(null);
      return;
    }

    try {
      const basePath = getTrackingBasePath(requestToProcess.type);
      const url = requestToProcess.request_type === 2
        ? `${DEFAULT_ENDPOINT_URL_EXPORT}/${basePath}/update/accept/${requestToProcess.token}?view=false`
        : `${DEFAULT_ENDPOINT_URL_EXPORT}/${basePath}/accept/${requestToProcess.token}?view=false`;
      await fetch(url);
      await queryClient.invalidateQueries({ queryKey: ['admin-requests'] });
      toastSuccess(requestToProcess.request_type === 2 ? "Änderung freigegeben" : "Antrag freigegeben");
      setApproveModalOpen(false);
      setRequestToProcess(null);
      setPendingDropdownId(null);
    } catch (error) {
      console.error('Network error:', error);
      toastError(requestToProcess.request_type === 2 ? "Änderung konnte nicht freigegeben werden" : "Antrag konnte nicht freigegeben werden");
    }
  };

  const handleRejectConfirm = async () => {
    if (!requestToProcess?.type || !requestToProcess?.token) return;

    try {
      const basePath = getTrackingBasePath(requestToProcess.type);
      const url = requestToProcess.request_type === 1
        ? `${DEFAULT_ENDPOINT_URL_EXPORT}/${basePath}/delete/reject/${requestToProcess.token}?view=false`
        : requestToProcess.request_type === 2
        ? `${DEFAULT_ENDPOINT_URL_EXPORT}/${basePath}/update/reject/${requestToProcess.token}?view=false`
        : `${DEFAULT_ENDPOINT_URL_EXPORT}/${basePath}/reject/${requestToProcess.token}?view=false`;
      await fetch(url);
      await queryClient.invalidateQueries({ queryKey: ['admin-requests'] });
      toastError(requestToProcess.request_type === 2 ? "Änderung abgelehnt" : "Antrag abgelehnt");
      setRejectModalOpen(false);
      setRequestToProcess(null);
      setPendingDropdownId(null);
    } catch (error) {
      toastError("Antrag konnte nicht abgelehnt werden");
      console.error('Network error:', error);
    }
  };

  const PENDING_GRID = 'grid-cols-[1.2fr_0.8fr_2fr_1fr_1fr_40px]';
  const APPROVED_GRID = 'grid-cols-[1.2fr_0.8fr_2fr_1fr_1fr_1fr_40px]';

  return (
    <div className="space-y-6">
      {/* Controls Row */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setRequestView('list')}
            className={`px-4 py-2 text-[16px] transition-colors relative ${requestView === 'list' ? 'text-[#0084c7] font-medium' : 'text-[#6b6b6b] hover:text-[#1c1c1c]'}`}
          >
            Liste
            {requestView === 'list' && <div className="absolute bottom-[-10px] left-0 right-0 h-[2px] bg-[#0084c7]" />}
          </button>
          <button
            onClick={() => setRequestView('calendar')}
            className={`px-4 py-2 text-[16px] transition-colors relative ${requestView === 'calendar' ? 'text-[#0084c7] font-medium' : 'text-[#6b6b6b] hover:text-[#1c1c1c]'}`}
          >
            Kalender
            {requestView === 'calendar' && <div className="absolute bottom-[-10px] left-0 right-0 h-[2px] bg-[#0084c7]" />}
          </button>
        </div>

        {requestView === 'list' && (
          <div className="flex items-center gap-2">
            {activeFilterCount > 0 && (
              <button
                onClick={resetFilters}
                className="px-3 py-2 text-[14px] text-[#6b6b6b] hover:text-[#0084c7] transition-colors"
              >
                Filter zurücksetzen
              </button>
            )}
            <button
              onClick={() => setFilterModalOpen(true)}
              className={`flex items-center gap-2 px-4 py-2 text-[14px] font-medium border rounded-lg transition-colors ${
                activeFilterCount > 0
                  ? 'border-[#0084c7] text-[#0084c7] bg-[#e8f4fb]'
                  : 'border-[#e0e0e0] text-[#1c1c1c] bg-white hover:border-[#0084c7]'
              }`}
            >
              <SlidersHorizontal className="size-4" />
              Filter
              {activeFilterCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[20px] h-[20px] px-1 rounded-full bg-[#0084c7] text-white text-[12px]">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>
        )}
      </div>

      {requestView === 'list' && (
        <div className="space-y-6">

          {/* OPEN REQUESTS */}
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <h2 className="text-[#1c1c1c] text-[18px] font-medium">Offene Anträge</h2>
              {filterChips.map((chip) => (
                <span
                  key={chip.key}
                  className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full text-[13px] bg-[#e8f4fb] text-[#0084c7] border border-[#b3dcf2]"
                >
                  {chip.label}
                  <button
                    onClick={chip.onRemove}
                    aria-label={`Filter „${chip.label}" entfernen`}
                    className="p-0.5 rounded-full hover:bg-[#d0eaf7]"
                  >
                    <X className="size-3.5" />
                  </button>
                </span>
              ))}
            </div>

            <div className="flex items-center gap-2 text-[#6b6b6b] text-[14px]">
              <span>Zeige</span>
              <select
                value={pendingItemsPerPage}
                onChange={(e) => { setPendingItemsPerPage(Number(e.target.value)); setPendingPage(1); }}
                className="form-select px-2 py-1 border border-[#e0e0e0] rounded"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
              <span>Einträge</span>
            </div>

            {/* Pending header */}
            <div className={`grid ${PENDING_GRID} gap-4 px-4 py-3 bg-[#f5f5f5] rounded-lg text-[14px] font-medium text-[#6b6b6b]`}>
              <div>Antragsteller</div>
              <div>Typ</div>
              <div>Zeitraum</div>
              <div>Vertretung</div>
              <div>Erstellt / Geändert</div>
              <div />
            </div>

            {pendingRequests?.length ? (
              pendingRequests.map((entry) => {
                const editPayload = parseEditPayload(entry.edit_payload);
                const userList = users ?? [];
                const substituteName = editPayload?.substitute_id !== undefined
                  ? userList.find(u => String(u.id) === String(editPayload.substitute_id))?.full_name ?? editPayload.substitute_id
                  : null;

                return (
                  <React.Fragment key={entry.id}>
                    <div
                      className={`grid ${PENDING_GRID} gap-4 px-4 py-3 text-[16px] items-start hover:bg-[#f5f5f5] transition-colors ${
                        entry.request_type === 1 ? 'border-l-4 border-red-500'
                        : entry.request_type === 2 ? 'border-l-4 border-blue-500'
                        : 'border-l-4 border-transparent'
                      }`}
                    >
                      <div className="text-[#1c1c1c] font-medium">{entry.user.full_name}</div>

                      <div className="text-[#6b6b6b]">{getRequestTypeLabel(entry.type)}</div>

                      <div className="text-[#6b6b6b] space-y-1">
                        <div>{formatDate(entry.start_date)} bis {formatDate(entry.end_date)}</div>
                        {editPayload?.start_date || editPayload?.end_date ? (
                          <div className="text-blue-500 text-[13px]">
                            → {formatDateShort(editPayload.start_date)} bis {formatDateShort(editPayload.end_date)}
                          </div>
                        ) : null}
                      </div>

                      <div className="text-[#6b6b6b] space-y-1">
                        <div>{entry.substitute ?? '-'}</div>
                        {editPayload && editPayload.substitute_id !== undefined && (
                          <div className="text-blue-500 text-[13px]">
                            → {substituteName ?? 'Kein Notdienst'}
                          </div>
                        )}
                      </div>

                      <div className="text-[#6b6b6b] text-[14px] space-y-1">
                        <div>{formatCreatedAt(entry.created_at)}</div>
                        {entry.updated_at && entry.updated_at !== entry.created_at && (
                          <div className="text-[12px] text-[#9ca3af]">
                            Geändert: {formatCreatedAt(entry.updated_at)}
                          </div>
                        )}
                      </div>

                      <div className="relative flex justify-end">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setPendingDropdownId(pendingDropdownId === entry.id ? null : entry.id);
                          }}
                          className="p-1 rounded-[6px] hover:bg-[#f5f5f5] transition-colors"
                        >
                          <MoreVertical className="size-5 text-[#6b6b6b]" />
                        </button>

                        {pendingDropdownId === entry.id && (
                          <div className="absolute right-0 top-8 bg-white border border-[#e9e9e9] rounded-[6px] shadow-lg z-10 min-w-[160px]">
                            {(entry.type === 'holiday' || entry.type === 'overtime') && !isRequestInPast(entry.end_date) && (
                              <button
                                onClick={() => handleEditClick(entry)}
                                className="w-full px-4 py-2.5 text-left text-[14px] hover:bg-[#f5f5f5]"
                              >
                                Bearbeiten
                              </button>
                            )}
                            <button
                              onClick={() => handleApproveClick(entry)}
                              className="w-full px-4 py-2.5 text-left text-[14px] hover:bg-[#f5f5f5]"
                            >
                              {entry.request_type === 1 ? 'Löschen bestätigen' : entry.request_type === 2 ? 'Änderung freigeben' : 'Freigeben'}
                            </button>
                            <button
                              onClick={() => handleRejectClick(entry)}
                              className="w-full px-4 py-2.5 text-left text-[14px] hover:bg-[#f5f5f5]"
                            >
                              Ablehnen
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {editPayload && (editPayload.comment || editPayload.reason) && (
                      <div className="px-4 pb-3 flex gap-x-6 text-[13px]">
                        {editPayload.comment && (
                          <div>
                            <span className="text-[#6b6b6b]">Kommentar: </span>
                            <span className="text-blue-600">{editPayload.comment}</span>
                          </div>
                        )}
                        {editPayload.reason && (
                          <div>
                            <span className="text-[#6b6b6b]">Grund: </span>
                            <span className="text-blue-600">{editPayload.reason}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <div className="px-4 py-6 text-[#6b6b6b] text-[14px] text-center border border-[#e0e0e0] rounded-lg bg-white">
                Keine Daten vorhanden
              </div>
            )}

            {/* Pending pagination */}
            <div className="flex items-center justify-between pt-4 border-t border-[#e0e0e0]">
              <div className="text-[#6b6b6b] text-[14px]">
                Zeige{' '}
                {Math.min(((pendingPaginator?.currentPage ?? 1) - 1) * pendingItemsPerPage + 1, pendingPaginator?.total ?? 0)}{' '}
                bis{' '}
                {Math.min((pendingPaginator?.currentPage ?? 1) * pendingItemsPerPage, pendingPaginator?.total ?? 0)}{' '}
                von {pendingPaginator?.total ?? 0} Einträgen
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPendingPage((prev) => Math.max(1, prev - 1))}
                  disabled={pendingPaginator?.currentPage === 1}
                  className="px-3 py-1 text-[14px] text-[#6b6b6b] hover:text-[#0084c7] disabled:opacity-50"
                >
                  Vorherige
                </button>
                {Array.from({ length: pendingPaginator?.lastPage ?? 1 }, (_, i) => i + 1)
                  .slice(Math.max(0, (pendingPaginator?.currentPage ?? 1) - 3), (pendingPaginator?.currentPage ?? 1) + 2)
                  .map((page) => (
                    <button
                      key={page}
                      onClick={() => setPendingPage(page)}
                      className={`px-3 py-1 rounded ${pendingPaginator?.currentPage === page ? 'bg-[#0084c7] text-white' : 'text-[#6b6b6b] hover:bg-[#f5f5f5]'}`}
                    >
                      {page}
                    </button>
                  ))}
                <button
                  onClick={() => setPendingPage((prev) => Math.min(pendingPaginator?.lastPage ?? prev, prev + 1))}
                  disabled={pendingPaginator?.currentPage === pendingPaginator?.lastPage}
                  className="px-3 py-1 text-[14px] text-[#6b6b6b] hover:text-[#0084c7] disabled:opacity-50"
                >
                  Nächste
                </button>
              </div>
            </div>
          </div>

          {/* APPROVED REQUESTS */}
          <div className="space-y-4">
            <h2 className="text-[#1c1c1c] text-[18px] font-medium">Frühere Anträge</h2>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-2 text-[#6b6b6b] text-[14px]">
                <span>Zeige</span>
                <select
                  value={approvedItemsPerPage}
                  onChange={(e) => { setApprovedItemsPerPage(Number(e.target.value)); setApprovedPage(1); }}
                  className="form-select px-2 py-1 border border-[#e0e0e0] rounded"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <span>Einträge</span>
              </div>

              <div className="relative">
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Antragsteller suchen…"
                  className="pl-9 pr-3 py-1.5 text-[14px] border border-[#e0e0e0] rounded-lg w-64 focus:outline-none focus:border-[#0084c7] transition-colors"
                />
                <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-[#6b6b6b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                </svg>
              </div>
            </div>

            {/* Approved header */}
            <div className={`grid ${APPROVED_GRID} gap-4 px-4 py-3 bg-[#f5f5f5] rounded-lg text-[14px] font-medium text-[#6b6b6b]`}>
              <div>Antragsteller</div>
              <div>Typ</div>
              <div>Zeitraum</div>
              <div>Vertretung</div>
              <div>Status</div>
              <div>Erstellt / Geändert</div>
              <div />
            </div>

            {approvedRequests?.length ? (
              approvedRequests.map((entry) => (
                <div
                  key={entry.id}
                  className={`grid ${APPROVED_GRID} gap-4 px-4 py-3 text-[16px] items-center hover:bg-[#f5f5f5] transition-colors`}
                >
                  <div className="text-[#1c1c1c]">{entry.user.full_name}</div>
                  <div className="text-[#6b6b6b]">{getRequestTypeLabel(entry.type)}</div>
                  <div className="text-[#6b6b6b]">{formatDate(entry.start_date)} bis {formatDate(entry.end_date)}</div>
                  <div className="text-[#6b6b6b]">{entry.substitute ?? '-'}</div>
                  <div>{getStatusBadge(entry.status)}</div>
                  <div className="text-[#6b6b6b] text-[14px] space-y-1">
                    <div>{formatCreatedAt(entry.created_at)}</div>
                    {entry.updated_at && entry.updated_at !== entry.created_at && (
                      <div className="text-[12px] text-[#9ca3af]">
                        Geändert: {formatCreatedAt(entry.updated_at)}
                      </div>
                    )}
                  </div>

                  <div className="relative flex justify-end">
                    <button
                      onClick={() => setOpenMenuId(openMenuId === entry.id ? null : entry.id)}
                      className="text-[#6b6b6b] hover:text-[#1c1c1c]"
                    >
                      <MoreVertical className="size-5" />
                    </button>
                    {openMenuId === entry.id && (
                      <div className="absolute right-0 top-8 bg-white border border-[#e9e9e9] rounded-[6px] shadow-lg z-10 min-w-[140px]">
                        {(entry.type === 'holiday' || entry.type === 'overtime') && (
                          <button
                            onClick={() => handleEditClick(entry)}
                            className="w-full px-4 py-2.5 text-left text-[14px] text-[#1c1c1c] hover:bg-[#f5f5f5] transition-colors"
                          >
                            Bearbeiten
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteClick(entry)}
                          className="w-full px-4 py-2.5 text-left text-[14px] text-[#1c1c1c] hover:bg-[#f5f5f5] transition-colors"
                        >
                          Löschen
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="px-4 py-6 text-[#6b6b6b] text-[14px] text-center border border-[#e0e0e0] rounded-lg bg-white">
                Keine Daten vorhanden
              </div>
            )}

            {/* Approved pagination */}
            <div className="flex items-center justify-between pt-4 border-t border-[#e0e0e0]">
              <div className="text-[#6b6b6b] text-[14px]">
                Zeige{' '}
                {Math.min(((approvedPaginator?.currentPage ?? 1) - 1) * approvedItemsPerPage + 1, approvedPaginator?.total ?? 0)}{' '}
                bis{' '}
                {Math.min((approvedPaginator?.currentPage ?? 1) * approvedItemsPerPage, approvedPaginator?.total ?? 0)}{' '}
                von {approvedPaginator?.total ?? 0} Einträgen
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setApprovedPage((prev) => Math.max(1, prev - 1))}
                  disabled={approvedPaginator?.currentPage === 1}
                  className="px-3 py-1 text-[14px] text-[#6b6b6b] hover:text-[#0084c7] disabled:opacity-50"
                >
                  Vorherige
                </button>
                {Array.from({ length: approvedPaginator?.lastPage ?? 1 }, (_, i) => i + 1)
                  .slice(Math.max(0, (approvedPaginator?.currentPage ?? 1) - 3), (approvedPaginator?.currentPage ?? 1) + 2)
                  .map((page) => (
                    <button
                      key={page}
                      onClick={() => setApprovedPage(page)}
                      className={`px-3 py-1 rounded ${approvedPaginator?.currentPage === page ? 'bg-[#0084c7] text-white' : 'text-[#6b6b6b] hover:bg-[#f5f5f5]'}`}
                    >
                      {page}
                    </button>
                  ))}
                <button
                  onClick={() => setApprovedPage((prev) => Math.min(approvedPaginator?.lastPage ?? prev, prev + 1))}
                  disabled={approvedPaginator?.currentPage === approvedPaginator?.lastPage}
                  className="px-3 py-1 text-[14px] text-[#6b6b6b] hover:text-[#0084c7] disabled:opacity-50"
                >
                  Nächste
                </button>
              </div>
            </div>
          </div>

        </div>
      )}

      {requestView === 'calendar' && (
        <RequestsCalendarView
          calendarDate={calendarDate}
          setCalendarDate={setCalendarDate}
          calendarView={calendarView}
          setCalendarView={setCalendarView}
        />
      )}

      <ConfirmModal
        isOpen={deleteRequestModalOpen}
        onClose={() => { setDeleteRequestModalOpen(false); setRequestToDelete(null); }}
        title="Antrag löschen"
        message={requestToDelete ? 'Möchten Sie den Antrag wirklich löschen?' : ''}
        onConfirm={() => {
          if (requestToDelete) {
            deleteRequest(
              { id: requestToDelete.id, type: mapTrackingTypeToEnum(requestToDelete.type) },
              {
                onSuccess: () => toastSuccess("Antrag gelöscht"),
                onError: () => toastError("Antrag konnte nicht gelöscht werden"),
              }
            );
          }
          setDeleteRequestModalOpen(false);
          setRequestToDelete(null);
          setOpenMenuId(null);
        }}
      />

      <ConfirmModal
        isOpen={approveModalOpen}
        onClose={() => { setApproveModalOpen(false); setRequestToProcess(null); }}
        title={
          requestToProcess?.request_type === 1 ? 'Löschantrag bestätigen'
          : requestToProcess?.request_type === 2 ? 'Änderungsantrag freigeben'
          : 'Antrag freigeben'
        }
        message={
          requestToProcess?.request_type === 1 ? 'Dieser Mitarbeiter möchte einen Eintrag löschen. Möchten Sie die Löschung wirklich bestätigen?'
          : requestToProcess?.request_type === 2 ? 'Dieser Mitarbeiter möchte einen Eintrag ändern. Möchten Sie die Änderung wirklich freigeben?'
          : 'Möchten Sie diesen Antrag wirklich freigeben?'
        }
        onConfirm={handleApproveConfirm}
      />

      <ConfirmModal
        isOpen={rejectModalOpen}
        onClose={() => { setRejectModalOpen(false); setRequestToProcess(null); }}
        title="Antrag ablehnen"
        message="Möchten Sie diesen Antrag wirklich ablehnen?"
        onConfirm={handleRejectConfirm}
      />

      {filterModalOpen && (
        <RequestsFilterModal
          filters={filters}
          userOptions={userOptions}
          onApply={applyFilters}
          onClose={() => setFilterModalOpen(false)}
        />
      )}

      {editModalEntry && (
        <EditRequestModal
          entry={editModalEntry}
          onClose={() => setEditModalEntry(null)}
        />
      )}
    </div>
  );
};